import React, { useId } from 'react';
import { useGridMode } from 'contexts/GridModeContext';
import { useIsActiveTable } from 'contexts/TableActiveContext';
import { useAppDispatch } from 'hooks';
import { acceptSitGoRematch, closeSitGoResultModal, isSitGoWin } from 'reducers/sitGoResult';
import { currencyFormatter } from 'utils/functions';

import GameModal, {
  btnRowStyle,
  primaryBtnStyle,
  secondaryBtnStyle
} from 'components/GameModal/GameModal';

import { useSitGoExit, useSitGoResultEntry } from './useSitGoResult';

import styles from './SitGoResult.module.scss';

export const SIT_GO_RESULT_COPY = {
  won: 'Congratulations, you won!',
  lost: 'Sorry, you lost',
  status: 'Sit & Go finished',
  thanks: 'Thanks for playing!',
  // Every format, heads-up included: with no seat held for the player yet
  // (EN-11139 AC 10), the prompt promises no instant rematch.
  prompt: 'Play Again?'
} as const;

/**
 * EN-11109: the Sit & Go end-of-game result, in the shared GameModal shell.
 * A view of this table's sitGoResult entry: opened when the player's result
 * arrives (SitGoResultWatcher), closed by the X / backdrop. The felt has no
 * way back to it until EN-11303; a Cancel on Confirm Registration, and a page
 * reload while it was open, bring it back.
 *
 * The offer never lapses: Yes stays enabled until the player chooses No, Yes
 * or Go to lobby, or leaves the table.
 *
 * Mounted beside TournamentScreenModal, which steps aside for a table that has
 * an entry. Renders nothing with release.sit-go-result-modal off.
 */
const SitGoResultModal = () => {
  const entry = useSitGoResultEntry();
  const exitSitGo = useSitGoExit();
  const dispatch = useAppDispatch();
  const isGridMode = useGridMode();
  const isActiveTable = useIsActiveTable();
  const titleId = `sit-go-result-title-${useId().replace(/:/g, '')}`;

  if (!entry) return null;

  const { gameID, outcome } = entry;
  const won = isSitGoWin(outcome);
  // App state can raise this while the player is on another table of the
  // carousel; gate on the panel being on screen so GameModal's rising-edge
  // ownership (useTableScopedOpen) latches when the player can see it.
  const open = entry.modalOpen && (isGridMode || isActiveTable);

  const handleClose = () => dispatch(closeSitGoResultModal(gameID));
  const handleNo = () => exitSitGo(outcome.capacity);
  const handleYes = () => dispatch(acceptSitGoRematch(gameID));

  return (
    <GameModal
      open={open}
      onClose={handleClose}
      showClose
      closeOnBackdropClick
      maxWidth='400px'
      ariaLabelledBy={titleId}>
      <div className={styles.body}>
        <h2 id={titleId} className={styles.title}>
          {won ? SIT_GO_RESULT_COPY.won : SIT_GO_RESULT_COPY.lost}
        </h2>
        {won && (
          <div className={styles.amount} data-testid='sit-go-result-amount'>
            {currencyFormatter(outcome.prize)}
          </div>
        )}
        <div className={styles.status}>{SIT_GO_RESULT_COPY.status}</div>
        {!won && <div className={styles.thanks}>{SIT_GO_RESULT_COPY.thanks}</div>}
        <div className={styles.prompt}>{SIT_GO_RESULT_COPY.prompt}</div>
        <div style={btnRowStyle}>
          <button type='button' style={secondaryBtnStyle} onClick={handleNo}>
            No
          </button>
          <button type='button' style={primaryBtnStyle} onClick={handleYes}>
            Yes
          </button>
        </div>
      </div>
    </GameModal>
  );
};

export default SitGoResultModal;
